"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Backend'in 429 cevabındaki `retry_after` saniyesini geri sayar.
 *
 * Demcas tarafında aynı telefon numarasına 3 dakikada en fazla 1 SMS
 * gönderiliyor (bkz. demcas `docs/sms.md`). Sınıra takılan istek 429 döner ve
 * gövdede kalan saniyeyi `retry_after` ile verir. Bu hook o saniyeyi geri
 * sayarak "tekrar gönder" butonunu kilitli tutar — kullanıcı boşuna tıklayıp
 * hata almasın.
 */
export function useSmsCooldown() {
  const [seconds, setSeconds] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clear = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(
    (from: number) => {
      const initial = Number.isFinite(from) && from > 0 ? Math.ceil(from) : 0;
      if (!initial) return;

      clear();
      setSeconds(initial);
      intervalRef.current = setInterval(() => {
        setSeconds((prev) => {
          if (prev <= 1) {
            clear();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [clear],
  );

  const reset = useCallback(() => {
    clear();
    setSeconds(0);
  }, [clear]);

  useEffect(() => clear, [clear]);

  return { seconds, start, reset, active: seconds > 0 };
}

/**
 * Bir API hatasından SMS hız sınırı saniyesini çıkarır.
 * 429 değilse null döner, yani çağıran taraf normal hata akışına devam eder.
 */
export function getSmsRetryAfter(error: any): number | null {
  if (!error || error.code !== 429) return null;

  const raw = error?.data?.retry_after;
  const parsed = Number(raw);

  // retry_after gelmezse (ör. eski backend) güvenli varsayılan: tam pencere.
  return Number.isFinite(parsed) && parsed > 0 ? Math.ceil(parsed) : 180;
}
